import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import EventDetailDialog from "@/components/EventDetailDialog";

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);

  const load = () => api.get("/events").then((r) => setEvents(r.data));

  useEffect(() => { load(); }, []);

  const now = Date.now();
  const isPast = (e) => new Date(e.date).getTime() < now - 6 * 60 * 60 * 1000;
  const upcoming = events.filter((e) => !isPast(e));
  const past = events
    .filter(isPast)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const renderCard = (e, past = false) => {
    const joined = user && e.rsvps?.includes(user.user_id);
    return (
      <EventDetailDialog key={e.event_id} event={e} onRsvpChange={load}>
        <button
          type="button"
          data-testid={`event-card-${e.event_id}`}
          className={`text-left w-full hairline overflow-hidden transition-all group ${
            past
              ? "bg-zinc-950 border-zinc-800 opacity-80 hover:opacity-100 hover:border-zinc-700"
              : "bg-zinc-900 hover:-translate-y-1 hover:border-orange-600/50"
          }`}
        >
          {e.image && (
            <div className={`aspect-[16/10] bg-zinc-800 overflow-hidden ${past ? "" : ""}`}>
              <img
                src={e.image}
                alt=""
                className={`w-full h-full object-cover transition duration-500 ${past ? "grayscale group-hover:grayscale-0" : "group-hover:scale-105"}`}
              />
            </div>
          )}
          <div className="p-6">
            <div className="flex items-center justify-between gap-2">
              <p className={`font-mono text-[10px] tracking-[0.25em] uppercase flex items-center gap-1 ${past ? "text-zinc-500" : "text-orange-500"}`}>
                <Calendar size={12} /> {new Date(e.date).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              </p>
              {past && (
                <span className="font-mono text-[8px] tracking-[0.25em] uppercase px-1.5 py-0.5 hairline border-zinc-700 text-zinc-500">
                  PAST
                </span>
              )}
            </div>
            <h3 className={`font-display text-2xl mt-2 transition ${past ? "text-zinc-300 group-hover:text-white" : "text-white group-hover:text-orange-400"}`}>{e.title}</h3>
            <p className={`text-sm mt-2 line-clamp-3 ${past ? "text-zinc-500" : "text-zinc-400"}`}>{e.description}</p>
            <div className={`mt-3 flex items-center gap-1 text-xs ${past ? "text-zinc-500" : "text-zinc-400"}`}>
              <MapPin size={12} /> {e.location}
              {e.distance_km ? <span className="ml-3 font-mono uppercase tracking-wider">· {e.distance_km} KM</span> : null}
            </div>
            <div className="mt-5 flex items-center justify-between">
              <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
                {(e.rsvps?.length || 0)} {past ? "WENT" : "GOING"}
              </p>
              {!past && (
                <span
                  data-testid={`rsvp-btn-${e.event_id}`}
                  className={`rounded-none font-display tracking-wider px-4 py-2 text-xs ${joined ? "bg-zinc-800 text-white" : "bg-orange-600 text-white"}`}
                >
                  {joined ? "GOING ✓" : "VIEW & RSVP"}
                </span>
              )}
            </div>
          </div>
        </button>
      </EventDetailDialog>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="events-page">
      <SEO title="Running Events in Odisha" path="/events"
        description="Upcoming running events across Odisha — long runs, hill repeats, marathons and group sessions from Rourkela to Puri. RSVP and run with the OSCR pack in person or virtually." />
      <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">CALENDAR</p>
      <h1 className="font-display text-4xl sm:text-6xl text-white mt-2">Events</h1>
      <p className="text-zinc-400 mt-3 max-w-2xl leading-relaxed">
        Discover upcoming running events across Odisha — from weekend long runs through the steel-city
        trails of Rourkela to dawn beach runs at Puri, hill repeats at Dhauli, group tempos in
        Bhubaneswar and full-distance marathons across the state. Whether you&apos;re a complete beginner
        looking for your first 5K, a seasoned half-marathoner, or an ultra runner chasing your next
        100K, there&apos;s a session for you. RSVP below to confirm your spot, meet your district pack
        and never miss a training day.
      </p>

      {/* UPCOMING */}
      <section className="mt-10">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
          <h2 className="font-display text-2xl text-white tracking-wider">UPCOMING</h2>
          <span className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
            {upcoming.length} ON THE CALENDAR
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {upcoming.length === 0 ? (
            <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm md:col-span-2 lg:col-span-3">
              No upcoming events scheduled. New runs drop weekly.
            </div>
          ) : (
            upcoming.map((e) => renderCard(e, false))
          )}
        </div>
      </section>

      {/* PAST */}
      {past.length > 0 && (
        <section className="mt-14" data-testid="past-events-list">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
            <h2 className="font-display text-2xl text-zinc-300 tracking-wider">PAST EVENTS</h2>
            <span className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
              {past.length} IN THE ARCHIVES
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {past.map((e) => renderCard(e, true))}
          </div>
        </section>
      )}
    </div>
  );
}
